/**
 * blockEditorRegistry: `{ type: EditorFormComponent }` map derived from the
 * canonical blockDefinitions module.
 *
 * Type ordering mirrors BLOCK_DEFINITIONS so all three registries remain in
 * sync. Editor forms are statically imported here (not lazy): the display
 * path (blockRegistry → blockDefinitions) never imports this file, so editor-
 * only code (TipTap, etc.) is excluded from the renderer bundle by module
 * boundaries rather than by dynamic import.
 *
 * Each form component receives a uniform `{ data, onChange }` interface:
 *   - `data`     : the block's current type-specific `data` payload.
 *   - `onChange` : called with the NEXT `data` object on every edit.
 */
import BLOCK_DEFINITIONS from '../blockDefinitions';

import HeroForm from './forms/HeroForm';
import HeadingForm from './forms/HeadingForm';
import ParagraphForm from './forms/ParagraphForm';
import ImageForm from './forms/ImageForm';
import ImageGridForm from './forms/ImageGridForm';
import StatForm from './forms/StatForm';
import QuoteForm from './forms/QuoteForm';
import CtaButtonForm from './forms/CtaButtonForm';
import EcosystemOrgsForm from './forms/EcosystemOrgsForm';
import EcosystemStatsForm from './forms/EcosystemStatsForm';
import GraphPreviewForm from './forms/GraphPreviewForm';
import CapabilityCardsForm from './forms/CapabilityCardsForm';
import LiveStatsForm from './forms/LiveStatsForm';
import LogoTextForm from './forms/LogoTextForm';
import EcosystemJoinForm from './forms/EcosystemJoinForm';
import RecentOrgsForm from './forms/RecentOrgsForm';

const FORM_MAP = {
    hero: HeroForm,
    heading: HeadingForm,
    paragraph: ParagraphForm,
    image: ImageForm,
    'image-grid': ImageGridForm,
    stat: StatForm,
    quote: QuoteForm,
    'cta-button': CtaButtonForm,
    'live-stats': LiveStatsForm,
    'logo-text': LogoTextForm,
    'ecosystem-join': EcosystemJoinForm,
    'ecosystem-orgs': EcosystemOrgsForm,
    'ecosystem-stats': EcosystemStatsForm,
    'graph-preview': GraphPreviewForm,
    'capability-cards': CapabilityCardsForm,
    'recent-orgs': RecentOrgsForm,
};

const blockEditorRegistry = Object.fromEntries(
    BLOCK_DEFINITIONS.map(({ type }) => [type, FORM_MAP[type]])
);

/**
 * Resolve the editor form component for a block type.
 *
 * @param {string} type - A block `type` value.
 * @returns {?React.ComponentType} The form component, or null if `type` is
 *   not a known block type.
 */
export const getBlockEditorForm = (type) => blockEditorRegistry[type] || null;

export default blockEditorRegistry;
